import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClipboardList,
  Search,
  Filter,
  ChevronRight,
  ChevronDown,
  Clock,
  Bot,
  Shield,
  Coins,
  FileSpreadsheet,
  Building2,
  Scale,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Timer,
  BarChart3,
  Play,
  RefreshCw,
  Eye,
  Calendar
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useTenantConfig } from '../contexts/TenantConfigContext';

// ============== Types ==============
interface ReasoningStep {
  id: number;
  executionId: string;
  agentType: string;
  agentName: string;
  stepNumber: number;
  stepType: 'ANALYSIS' | 'DECISION' | 'RECOMMENDATION' | 'ACTION' | 'VALIDATION';
  reasoningTitle: string;
  reasoningDetail: string;
  metricType?: 'TAX_SAVINGS' | 'TIME_SAVED' | 'RISK_REDUCED' | 'COMPLIANCE_SCORE';
  metricValue?: number;
  confidenceScore?: number;
  inputData?: any;
  outputData?: any;
  createdAt: string;
}

interface ExecutionSummary {
  executionId: string;
  agentType: string;
  agentName: string;
  totalSteps: number;
  metrics: {
    totalTaxSavings: number;
    totalTimeSavedMinutes: number;
    recommendationsGenerated: number;
    validationsPerformed: number;
  };
  reasoningTrail: ReasoningStep[];
}

interface TaxAlphaOpportunity {
  portfolioId: number;
  portfolioName: string;
  securityId: string;
  securitySymbol: string;
  unrealizedLoss: number;
  estimatedTaxSavings: number;
  holdingPeriod: string;
  reasoning: string;
}

// ============== Styled Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 24px;
`;

const HeaderSection = styled.div`
  margin-bottom: 24px;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const MetricCard = styled(GlassCard)`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepIcon = ({ type }: { type: string }) => {
  const iconClass = "w-4 h-4";
  switch (type) {
    case 'ANALYSIS':
      return <BarChart3 className={cn(iconClass, "text-blue-400")} />;
    case 'DECISION':
      return <CheckCircle className={cn(iconClass, "text-emerald-400")} />;
    case 'RECOMMENDATION':
      return <TrendingUp className={cn(iconClass, "text-amber-400")} />;
    case 'ACTION':
      return <Play className={cn(iconClass, "text-violet-400")} />;
    case 'VALIDATION':
      return <Shield className={cn(iconClass, "text-indigo-400")} />;
    default:
      return <Bot className={cn(iconClass, "text-slate-400")} />;
  }
};

const AgentIcon = ({ agentType }: { agentType: string }) => {
  const iconClass = "w-5 h-5";
  switch (agentType) {
    case 'TAX_HARVESTER':
      return <Coins className={cn(iconClass, "text-emerald-400")} />;
    case 'GOVERNANCE':
      return <Scale className={cn(iconClass, "text-rose-400")} />;
    case 'COMPLIANCE_CHECKER':
      return <Shield className={cn(iconClass, "text-indigo-400")} />;
    case 'PORTFOLIO_REBALANCER':
      return <BarChart3 className={cn(iconClass, "text-blue-400")} />;
    case 'K1_PROCESSOR':
      return <FileSpreadsheet className={cn(iconClass, "text-amber-400")} />;
    case 'ALTS_EXECUTOR':
      return <Building2 className={cn(iconClass, "text-violet-400")} />;
    default:
      return <Bot className={cn(iconClass, "text-slate-400")} />;
  }
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AuditTrailViewer: React.FC = () => {
  const { isRetail } = useTenantConfig();
  const [activeTab, setActiveTab] = useState<'recent' | 'tax-alpha' | 'governance'>('recent');
  const [recentReasoning, setRecentReasoning] = useState<ReasoningStep[]>([]);
  const [taxSavings, setTaxSavings] = useState<any>({});
  const [selectedExecution, setSelectedExecution] = useState<ExecutionSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [taxOpportunities, setTaxOpportunities] = useState<TaxAlphaOpportunity[]>([]);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAgent, setFilterAgent] = useState<string>('all');

  // Fetch recent reasoning on mount
  useEffect(() => {
    fetchRecentReasoning();
    fetchTaxSavings();
  }, []);

  const fetchRecentReasoning = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/agents/audit/recent?size=50');
      setRecentReasoning(response.data.content || []);
    } catch (error) {
      console.error('Failed to fetch reasoning:', error);
      // Mock data for demo
      setRecentReasoning(generateMockReasoning());
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTaxSavings = async () => {
    try {
      const response = await axios.get('/api/agents/audit/tax-savings?days=30');
      setTaxSavings(response.data);
    } catch (error) {
      console.error('Failed to fetch tax savings:', error);
      // Mock data
      setTaxSavings({
        TAX_HARVESTER: 142500,
        GOVERNANCE: 0,
        COMPLIANCE_CHECKER: 0,
        periodDays: 30
      });
    }
  };

  const fetchExecutionDetail = async (executionId: string) => {
    try {
      const response = await axios.get(`/api/agents/audit/execution/${executionId}`);
      setSelectedExecution(response.data);
    } catch (error) {
      console.error('Failed to fetch execution:', error);
    }
  };

  const triggerTaxAlphaAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      const response = await axios.post('/api/agents/audit/tax-alpha/analyze');
      setTaxOpportunities(response.data.tlhOpportunities || []);
      // Refresh reasoning after analysis
      fetchRecentReasoning();
    } catch (error) {
      console.error('Failed to run tax alpha analysis:', error);
      // Mock opportunities for demo
      setTaxOpportunities([
        {
          portfolioId: 1,
          portfolioName: 'Smith Family Trust A',
          securityId: 'US0378331005',
          securitySymbol: 'AAPL',
          unrealizedLoss: -15000,
          estimatedTaxSavings: 5550,
          holdingPeriod: 'SHORT_TERM',
          reasoning: 'I recommend harvesting the loss in AAPL held in Trust A. Unrealized Loss: $15,000 (short-term). Estimated Tax Savings: $5,550 (at 37% rate). No wash sale risk detected - no AAPL purchases in past 30 days.'
        },
        {
          portfolioId: 2,
          portfolioName: 'Chen Family Revocable',
          securityId: 'US88160R1014',
          securitySymbol: 'TSLA',
          unrealizedLoss: -8500,
          estimatedTaxSavings: 3145,
          holdingPeriod: 'SHORT_TERM',
          reasoning: 'I recommend harvesting the loss in TSLA. Unrealized Loss: $8,500 (short-term). Estimated Tax Savings: $3,145 (at 37% rate). Consider replacing with RIVN for similar EV exposure.'
        }
      ]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleStepExpand = (stepId: number) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  // Filter reasoning by search and agent
  const filteredReasoning = recentReasoning.filter(step => {
    const matchesSearch = searchTerm === '' ||
      step.reasoningTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      step.reasoningDetail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAgent = filterAgent === 'all' || step.agentType === filterAgent;
    return matchesSearch && matchesAgent;
  });

  // Group reasoning by execution
  const groupedByExecution = filteredReasoning.reduce((acc, step) => {
    if (!acc[step.executionId]) {
      acc[step.executionId] = [];
    }
    acc[step.executionId].push(step);
    return acc;
  }, {} as Record<string, ReasoningStep[]>);

  return (
    <Container>
      {/* Header */}
      <HeaderSection>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <ClipboardList className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Agent Audit Trail</h1>
              <p className="text-slate-400 text-sm">Investment Committee-ready explainable AI reasoning</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchRecentReasoning}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-white/5 rounded-lg text-sm font-medium text-slate-300 hover:text-white transition-all flex items-center gap-2"
            >
              <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
              Refresh
            </button>
            {isRetail && (
              <button
                onClick={triggerTaxAlphaAnalysis}
                disabled={isAnalyzing}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold text-white transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/20 disabled:opacity-50"
              >
                <Coins className={cn("w-4 h-4", isAnalyzing && "animate-spin")} />
                {isAnalyzing ? 'Analyzing...' : 'Run Tax Alpha Scan'}
              </button>
            )}
          </div>
        </div>

        {/* Metrics Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <MetricCard>
            <div className="flex items-center gap-2 text-slate-400">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Tax Savings (30d)</span>
            </div>
            <div className="text-2xl font-bold text-emerald-400">
              {formatCurrency(taxSavings.TAX_HARVESTER || 0)}
            </div>
            <div className="text-xs text-slate-500">Generated by Tax Alpha Agent</div>
          </MetricCard>

          <MetricCard>
            <div className="flex items-center gap-2 text-slate-400">
              <Timer className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Time Saved (30d)</span>
            </div>
            <div className="text-2xl font-bold text-blue-400">47.5 hrs</div>
            <div className="text-xs text-slate-500">Automated analysis & validation</div>
          </MetricCard>

          <MetricCard>
            <div className="flex items-center gap-2 text-slate-400">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Validations</span>
            </div>
            <div className="text-2xl font-bold text-violet-400">1,247</div>
            <div className="text-xs text-slate-500">Pre-trade governance checks</div>
          </MetricCard>

          <MetricCard>
            <div className="flex items-center gap-2 text-slate-400">
              <Shield className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Violations Blocked</span>
            </div>
            <div className="text-2xl font-bold text-rose-400">23</div>
            <div className="text-xs text-slate-500">IPS violations prevented</div>
          </MetricCard>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 bg-slate-900/50 rounded-lg w-fit">
          {[
            { id: 'recent', label: 'Recent Reasoning', icon: Clock },
            { id: 'tax-alpha', label: 'Tax Alpha Opportunities', icon: Coins },
            { id: 'governance', label: 'Governance Checks', icon: Shield }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2",
                activeTab === tab.id
                  ? "bg-indigo-600 text-white shadow-lg"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </HeaderSection>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left: Reasoning List */}
        <div className="col-span-2">
          <GlassCard>
            {/* Search & Filter */}
            <div className="p-4 border-b border-white/5 flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search reasoning..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-white/5 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/50"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <select
                  value={filterAgent}
                  onChange={(e) => setFilterAgent(e.target.value)}
                  className="px-3 py-2 bg-slate-900 border border-white/5 rounded-lg text-sm text-white focus:outline-none focus:border-indigo-500/50"
                >
                  <option value="all">All Agents</option>
                  <option value="TAX_HARVESTER">Tax Alpha</option>
                  <option value="GOVERNANCE">Governance</option>
                  <option value="COMPLIANCE_CHECKER">Compliance</option>
                  <option value="PORTFOLIO_REBALANCER">Rebalancer</option>
                </select>
              </div>
            </div>

            {/* Reasoning Steps */}
            <div className="max-h-[600px] overflow-y-auto">
              {activeTab === 'recent' && (
                <AnimatePresence>
                  {Object.entries(groupedByExecution).map(([execId, steps]) => (
                    <motion.div
                      key={execId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border-b border-white/5 last:border-b-0"
                    >
                      {/* Execution Header */}
                      <div
                        onClick={() => fetchExecutionDetail(execId)}
                        className="p-4 hover:bg-white/5 cursor-pointer transition-colors flex items-center gap-4"
                      >
                        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center">
                          <AgentIcon agentType={steps[0]?.agentType || ''} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-white">
                              {steps[0]?.agentName || 'Unknown Agent'}
                            </span>
                            <span className="text-xs text-slate-500">•</span>
                            <span className="text-xs text-slate-500">
                              {formatDate(steps[0]?.createdAt || '')}
                            </span>
                          </div>
                          <div className="text-sm text-slate-400 truncate">
                            {steps[0]?.reasoningTitle}
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-slate-500">
                              {steps.length} step{steps.length !== 1 ? 's' : ''}
                            </span>
                            {steps.some(s => s.metricType === 'TAX_SAVINGS') && (
                              <span className="text-xs text-emerald-400 font-medium">
                                {formatCurrency(steps.filter(s => s.metricType === 'TAX_SAVINGS').reduce((sum, s) => sum + (s.metricValue || 0), 0))} savings
                              </span>
                            )}
                          </div>
                        </div>
                        <Eye className="w-4 h-4 text-slate-500" />
                      </div>

                      {/* Expanded Steps */}
                      {selectedExecution?.executionId === execId && (
                        <div className="px-4 pb-4 space-y-2">
                          {steps.sort((a, b) => a.stepNumber - b.stepNumber).map((step, idx) => (
                            <div
                              key={step.id}
                              className="relative pl-8 border-l-2 border-slate-700"
                            >
                              {/* Step indicator */}
                              <div className="absolute -left-[9px] top-2 w-4 h-4 rounded-full bg-slate-800 border-2 border-slate-600 flex items-center justify-center">
                                <StepIcon type={step.stepType} />
                              </div>

                              <div
                                onClick={() => toggleStepExpand(step.id)}
                                className="p-3 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-colors"
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <span className={cn(
                                      "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded",
                                      step.stepType === 'ANALYSIS' && "bg-blue-500/20 text-blue-400",
                                      step.stepType === 'DECISION' && "bg-emerald-500/20 text-emerald-400",
                                      step.stepType === 'RECOMMENDATION' && "bg-amber-500/20 text-amber-400",
                                      step.stepType === 'ACTION' && "bg-violet-500/20 text-violet-400",
                                      step.stepType === 'VALIDATION' && "bg-indigo-500/20 text-indigo-400"
                                    )}>
                                      {step.stepType}
                                    </span>
                                    <span className="text-sm font-medium text-white">{step.reasoningTitle}</span>
                                  </div>
                                  {expandedSteps.has(step.id) ? (
                                    <ChevronDown className="w-4 h-4 text-slate-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-slate-500" />
                                  )}
                                </div>

                                {expandedSteps.has(step.id) && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="mt-3 space-y-3"
                                  >
                                    <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                                      {step.reasoningDetail}
                                    </p>

                                    {step.metricValue && (
                                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                                        <DollarSign className="w-4 h-4 text-emerald-400" />
                                        <span className="text-sm font-bold text-emerald-400">
                                          {step.metricType === 'TAX_SAVINGS' && formatCurrency(step.metricValue)}
                                          {step.metricType === 'TIME_SAVED' && `${step.metricValue} min saved`}
                                        </span>
                                      </div>
                                    )}

                                    {step.confidenceScore && (
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-500">Confidence:</span>
                                        <div className="w-24 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                          <div
                                            className="h-full bg-indigo-500 rounded-full"
                                            style={{ width: `${step.confidenceScore * 100}%` }}
                                          />
                                        </div>
                                        <span className="text-xs text-slate-400">{Math.round(step.confidenceScore * 100)}%</span>
                                      </div>
                                    )}
                                  </motion.div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}

              {activeTab === 'tax-alpha' && (
                <div className="p-4 space-y-4">
                  {taxOpportunities.length === 0 ? (
                    <div className="text-center py-12">
                      <Coins className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400 mb-2">No tax-loss harvesting opportunities found</p>
                      <p className="text-xs text-slate-600">Click "Run Tax Alpha Scan" to analyze portfolios</p>
                    </div>
                  ) : (
                    taxOpportunities.map((opp, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="p-4 bg-slate-900/50 border border-white/5 rounded-xl"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg font-bold text-white">{opp.securitySymbol}</span>
                              <span className={cn(
                                "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded",
                                opp.holdingPeriod === 'SHORT_TERM' ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"
                              )}>
                                {opp.holdingPeriod.replace('_', ' ')}
                              </span>
                            </div>
                            <p className="text-sm text-slate-400">{opp.portfolioName}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-rose-400">{formatCurrency(opp.unrealizedLoss)}</div>
                            <div className="text-xs text-slate-500">Unrealized Loss</div>
                          </div>
                        </div>

                        <div className="p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-lg mb-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-400">Estimated Tax Savings</span>
                            <span className="text-lg font-bold text-emerald-400">{formatCurrency(opp.estimatedTaxSavings)}</span>
                          </div>
                        </div>

                        <div className="p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Bot className="w-4 h-4 text-indigo-400" />
                            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">AI Reasoning</span>
                          </div>
                          <p className="text-sm text-slate-300 leading-relaxed">{opp.reasoning}</p>
                        </div>

                        <div className="flex items-center gap-2 mt-4">
                          <button className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold text-white transition-colors">
                            Stage Harvest Trade
                          </button>
                          <button className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-white/5 rounded-lg text-sm font-medium text-slate-300 transition-colors">
                            Dismiss
                          </button>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'governance' && (
                <div className="p-4">
                  <div className="text-center py-12">
                    <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400 mb-2">Governance checks happen automatically on trade submission</p>
                    <p className="text-xs text-slate-600">View recent trade validations in the "Recent Reasoning" tab</p>
                  </div>
                </div>
              )}
            </div>
          </GlassCard>
        </div>

        {/* Right: Execution Detail */}
        <div className="col-span-1">
          <GlassCard className="sticky top-4">
            <div className="p-4 border-b border-white/5">
              <h3 className="text-sm font-bold text-white">Execution Detail</h3>
            </div>

            {selectedExecution ? (
              <div className="p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                    <AgentIcon agentType={selectedExecution.agentType} />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">{selectedExecution.agentName}</div>
                    <div className="text-xs text-slate-500">{selectedExecution.executionId}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-1">Total Steps</div>
                    <div className="text-xl font-bold text-white">{selectedExecution.totalSteps}</div>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-1">Tax Savings</div>
                    <div className="text-xl font-bold text-emerald-400">
                      {formatCurrency(selectedExecution.metrics?.totalTaxSavings || 0)}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-1">Time Saved</div>
                    <div className="text-xl font-bold text-blue-400">
                      {selectedExecution.metrics?.totalTimeSavedMinutes || 0} min
                    </div>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-1">Recommendations</div>
                    <div className="text-xl font-bold text-amber-400">
                      {selectedExecution.metrics?.recommendationsGenerated || 0}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-white/5">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Reasoning Timeline</h4>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {selectedExecution.reasoningTrail?.map((step, idx) => (
                      <div key={step.id} className="flex items-start gap-2 text-xs">
                        <StepIcon type={step.stepType} />
                        <div>
                          <div className="text-slate-300">{step.reasoningTitle}</div>
                          <div className="text-slate-600">Step {step.stepNumber}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Eye className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Select an execution to view details</p>
              </div>
            )}
          </GlassCard>
        </div>
      </div>
    </Container>
  );
};

// Mock data generator for demo
function generateMockReasoning(): ReasoningStep[] {
  const now = new Date();
  return [
    {
      id: 1,
      executionId: 'exec-2024-001',
      agentType: 'TAX_HARVESTER',
      agentName: 'Tax Alpha Agent',
      stepNumber: 1,
      stepType: 'ANALYSIS',
      reasoningTitle: 'Scanning Smith Family portfolios for TLH opportunities',
      reasoningDetail: 'I analyzed 47 tax lots across 5 accounts (Trust A, Trust B, Revocable, IRA-1, IRA-2). I found 3 positions with unrealized losses exceeding $5,000 that are eligible for harvesting. I excluded IRA accounts as they do not benefit from tax-loss harvesting.',
      confidenceScore: 0.95,
      createdAt: new Date(now.getTime() - 30 * 60000).toISOString()
    },
    {
      id: 2,
      executionId: 'exec-2024-001',
      agentType: 'TAX_HARVESTER',
      agentName: 'Tax Alpha Agent',
      stepNumber: 2,
      stepType: 'DECISION',
      reasoningTitle: 'Selected Trust A for AAPL harvest',
      reasoningDetail: 'I selected Trust A for the AAPL loss harvest because it has the highest cost-basis lots ($185/share vs $142/share in Trust B). Harvesting from Trust A maximizes the loss capture at $15,000 vs $8,200 from Trust B.',
      confidenceScore: 0.92,
      createdAt: new Date(now.getTime() - 28 * 60000).toISOString()
    },
    {
      id: 3,
      executionId: 'exec-2024-001',
      agentType: 'TAX_HARVESTER',
      agentName: 'Tax Alpha Agent',
      stepNumber: 3,
      stepType: 'VALIDATION',
      reasoningTitle: 'Checking wash sale risk across all entities',
      reasoningDetail: 'I verified no AAPL purchases occurred in the past 30 days across all 5 accounts. I also checked scheduled purchases and found none planned for the next 30 days. No wash sale risk detected for this harvest.',
      confidenceScore: 0.98,
      createdAt: new Date(now.getTime() - 25 * 60000).toISOString()
    },
    {
      id: 4,
      executionId: 'exec-2024-001',
      agentType: 'TAX_HARVESTER',
      agentName: 'Tax Alpha Agent',
      stepNumber: 4,
      stepType: 'RECOMMENDATION',
      reasoningTitle: 'Recommending AAPL harvest with replacement',
      reasoningDetail: 'I recommend harvesting the $15,000 loss in AAPL and replacing with MSFT to maintain tech sector exposure. Estimated tax savings: $5,550 at 37% marginal rate. The replacement maintains similar beta (1.2 vs 1.1) and market cap exposure.',
      metricType: 'TAX_SAVINGS',
      metricValue: 5550,
      confidenceScore: 0.89,
      createdAt: new Date(now.getTime() - 22 * 60000).toISOString()
    },
    {
      id: 5,
      executionId: 'exec-2024-002',
      agentType: 'GOVERNANCE',
      agentName: 'Governance Agent',
      stepNumber: 1,
      stepType: 'VALIDATION',
      reasoningTitle: 'Pre-trade validation for Smith Trust A trade',
      reasoningDetail: 'I validated the proposed AAPL sell order ($165,000 notional) against Investment Policy Statement rules. Checked: Single security concentration (PASS - 4.2% vs 10% limit), Sector concentration (PASS - Tech at 28% vs 35% limit), Liquidity (PASS - 3x daily volume), ESG exclusion list (PASS - not excluded).',
      confidenceScore: 0.99,
      createdAt: new Date(now.getTime() - 15 * 60000).toISOString()
    },
    {
      id: 6,
      executionId: 'exec-2024-002',
      agentType: 'GOVERNANCE',
      agentName: 'Governance Agent',
      stepNumber: 2,
      stepType: 'DECISION',
      reasoningTitle: 'Trade approved with no warnings',
      reasoningDetail: 'All 12 IPS rules passed validation. Trade is approved for execution. No additional review required.',
      metricType: 'TIME_SAVED',
      metricValue: 15,
      confidenceScore: 0.99,
      createdAt: new Date(now.getTime() - 14 * 60000).toISOString()
    }
  ];
}

export default AuditTrailViewer;
